import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, Column } from '@/components/ui/data-table';
import { getSchools, getSchoolsStats, getAcademies } from '@/api/schools';
import { getSchoolNetworks } from '@/api/school-networks';
import type { SchoolNetwork } from '@/types/school-network';
import type { School, SchoolsStats, SchoolFilters, Academie } from '@/types/school';
import {
  Plus,
  Star,
  ExternalLink,
  Eye,
  MoreHorizontal,
  CheckCircle2,
  Clock,
  PlayCircle,
  XCircle,
  Users,
  LogIn,
  Search,
  Server,
  Cloud,
  School as SchoolIcon,
  MapPin,
  RefreshCw,
  Loader2,
  ChevronRight,
  Building2,
} from 'lucide-react';
import { ExportButton } from '@/components/export/ExportButton';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/api/client';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'green' | 'orange' | 'red';
  onClick?: () => void;
  isActive?: boolean;
}

function StatCard({ title, value, icon: Icon, color, onClick, isActive }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
    red: 'bg-red-50 text-red-600 border-red-200',
  };

  const activeColorClasses = {
    blue: 'ring-2 ring-blue-500',
    green: 'ring-2 ring-emerald-500',
    orange: 'ring-2 ring-orange-500',
    red: 'ring-2 ring-red-500',
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isActive && activeColorClasses[color]
      )}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <div className={cn('rounded-lg p-3', colorClasses[color])}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('fr-FR').format(value);
}

export function SchoolsPage() {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [schools, setSchools] = useState<School[]>([]);
  const [stats, setStats] = useState<SchoolsStats | null>(null);
  const [networks, setNetworks] = useState<SchoolNetwork[]>([]);
  const [academies, setAcademies] = useState<Academie[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Get filters from URL params
  const filters: SchoolFilters = useMemo(
    () => ({
      status: searchParams.get('status') || 'active',
      vip: searchParams.get('vip') || '',
      version: searchParams.get('version') || '',
      network: searchParams.get('network') || '',
      academie: searchParams.get('academie') || '',
      favorites: searchParams.get('favorites') || '',
      search: searchParams.get('search') || '',
      page: Number(searchParams.get('page')) || 1,
      limit: Number(searchParams.get('limit')) || 25,
      sortBy: searchParams.get('sortBy') || 'createdAt',
      sortOrder: (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC',
    }),
    [searchParams]
  );

  // Update filters
  const updateFilters = useCallback(
    (newFilters: Partial<SchoolFilters>) => {
      const updated = { ...filters, ...newFilters };
      const params = new URLSearchParams();

      if (updated.status && updated.status !== 'active') params.set('status', updated.status);
      if (updated.vip) params.set('vip', updated.vip);
      if (updated.version) params.set('version', updated.version);
      if (updated.network) params.set('network', updated.network);
      if (updated.academie) params.set('academie', updated.academie);
      if (updated.favorites) params.set('favorites', updated.favorites);
      if (updated.search) params.set('search', updated.search);
      if (updated.page && updated.page !== 1) params.set('page', updated.page.toString());
      if (updated.limit && updated.limit !== 25) params.set('limit', updated.limit.toString());
      if (updated.sortBy && updated.sortBy !== 'createdAt') params.set('sortBy', updated.sortBy);
      if (updated.sortOrder && updated.sortOrder !== 'DESC') params.set('sortOrder', updated.sortOrder);

      setSearchParams(params);
    },
    [filters, setSearchParams]
  );

  // Fetch data
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [schoolsResponse, statsResponse, networksResponse, academiesResponse] = await Promise.all([
          getSchools(filters),
          getSchoolsStats(),
          getSchoolNetworks(),
          getAcademies(),
        ]);

        setSchools(schoolsResponse.data);
        setTotalItems(schoolsResponse.meta.total);
        setTotalPages(schoolsResponse.meta.totalPages);
        setStats(statsResponse);
        setNetworks(networksResponse);
        setAcademies(academiesResponse);
      } catch (error) {
        console.error('Error fetching schools:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [filters]);

  // Handle sorting
  const handleSort = (key: string) => {
    const newOrder =
      filters.sortBy === key && filters.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    updateFilters({ sortBy: key, sortOrder: newOrder, page: 1 });
  };

  // Configuration sync state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<'ok' | 'nok' | null>(null);

  const handleSyncConfiguration = useCallback(async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const response = await apiClient.get('/api/configuration/push');
      setSyncResult(response.data.result === 'ok' ? 'ok' : 'nok');
    } catch {
      setSyncResult('nok');
    } finally {
      setSyncLoading(false);
    }
  }, []);

  // Écoles dépliées (sous-liste des établissements secondaires)
  const [expandedSchools, setExpandedSchools] = useState<Set<number>>(new Set());

  const toggleExpanded = useCallback((schoolId: number) => {
    setExpandedSchools((prev) => {
      const next = new Set(prev);
      if (next.has(schoolId)) {
        next.delete(schoolId);
      } else {
        next.add(schoolId);
      }
      return next;
    });
  }, []);

  // Handle search with debounce
  const [searchInput, setSearchInput] = useState(filters.search || '');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilters({ search: searchInput, page: 1 });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, updateFilters]);

  // Table columns
  const columns: Column<School>[] = useMemo(
    () => [
      {
        key: 'favorite',
        header: (
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateFilters({ favorites: filters.favorites === 'true' ? '' : 'true', page: 1 });
            }}
            className={cn(
              'p-1 rounded transition-colors',
              filters.favorites === 'true' ? 'bg-yellow-100' : 'hover:bg-muted',
            )}
            title={filters.favorites === 'true' ? 'Afficher toutes les écoles' : 'Afficher mes favoris'}
          >
            <Star className={cn('h-4 w-4', filters.favorites === 'true' ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400')} />
          </button>
        ),
        className: 'w-8 px-0',
        render: (school) => {
          const fav = isFavorite(school.id);
          return (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite(school.code);
              }}
              className="p-1 rounded hover:bg-muted transition-colors"
              title={fav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <Star className={cn('h-4 w-4', fav ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300')} />
            </button>
          );
        },
      },
      {
        key: 'logo',
        header: '',
        className: 'w-8 px-0',
        render: (school) => (
          <Link to={`/schools/${school.code}`}>
            {school.logoUrl ? (
              <img
                src={school.logoUrl}
                alt={school.name}
                className="h-12 w-12 rounded object-contain hover:opacity-80 transition-opacity"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                <SchoolIcon className="h-6 w-6 text-gray-400" />
              </div>
            )}
          </Link>
        ),
      },
      {
        key: 'code',
        header: 'Code',
        sortable: true,
        render: (school) => (
          <Link
            to={`/schools/${school.code}`}
            className="font-mono text-sm text-gray-600 hover:text-primary hover:underline"
          >
            {school.code}
          </Link>
        ),
      },
      {
        key: 'name',
        header: 'Nom',
        sortable: true,
        render: (school) => {
          const secondaryCount = school.establishments?.filter((e) => !e.isPrincipal).length ?? 0;
          return (
            <div className="flex items-center gap-2">
              <Link
                to={`/schools/${school.code}`}
                className="font-medium text-gray-900 hover:text-primary hover:underline"
              >
                {school.name}
              </Link>
              {school.vip && (
                <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 border border-amber-300">
                  VIP
                </span>
              )}
              {secondaryCount > 0 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleExpanded(school.id);
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                  title={expandedSchools.has(school.id) ? 'Masquer les établissements' : 'Afficher les établissements'}
                >
                  <Building2 className="h-3 w-3" />
                  {secondaryCount + 1} établissements
                  <ChevronRight
                    className={cn(
                      'h-3 w-3 transition-transform',
                      expandedSchools.has(school.id) && 'rotate-90'
                    )}
                  />
                </button>
              )}
            </div>
          );
        },
      },
      {
        key: 'v1',
        header: 'V1',
        sortable: true,
        className: 'text-center w-[150px]',
        render: (school) => {
          if (!school.hasV1) return <span className="text-xs text-gray-400">-</span>;
          const dep = school.deployments.find((d) => d.version === 'v1');
          return (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Server className="h-3 w-3 mr-1" />
              {dep?.statusLabel || 'V1'}
            </Badge>
          );
        },
      },
      {
        key: 'v2',
        header: 'V2',
        sortable: true,
        className: 'text-center w-[150px]',
        render: (school) => {
          if (!school.hasV2) return <span className="text-xs text-gray-400">-</span>;
          const dep = school.deployments.find((d) => d.version === 'v2');
          return (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <Cloud className="h-3 w-3 mr-1" />
              {dep?.statusLabel || 'V2'}
            </Badge>
          );
        },
      },
      {
        key: 'studentCount',
        header: 'Élèves',
        sortable: true,
        className: 'text-center',
        render: (school) => (
          <div className="flex items-center justify-center gap-1 text-sm text-gray-600">
            <Users className="h-3.5 w-3.5" />
            {formatNumber(school.studentCount)}
          </div>
        ),
      },
      {
        key: 'connexionCount',
        header: 'Connexions',
        sortable: true,
        className: 'text-center',
        render: (school) => (
          <div className="flex items-center justify-center gap-1 text-sm text-gray-600">
            <LogIn className="h-3.5 w-3.5" />
            {formatNumber(school.connexionCount)}
          </div>
        ),
      },
      {
        key: 'support',
        header: 'Support',
        sortable: true,
        render: (school) => (
          <span className="text-sm text-gray-600">{school.supportLabel}</span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Creation',
        sortable: true,
        render: (school) => (
          <span className="text-sm text-gray-600">
            {formatDate(school.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        className: 'w-10',
        render: (school) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/schools/${school.code}`)}>
                <Eye className="mr-2 h-4 w-4" />
                Voir
              </DropdownMenuItem>
              {school.hasV1 && (
                <DropdownMenuItem onClick={() => {
                  const url = school.whiteLabel
                    ? `https://${school.whiteLabel}`
                    : `https://app.ecole-futee.com/signin/index?sclid=${school.code}`;
                  window.open(url, '_blank');
                }}>
                  <ExternalLink className="mr-2 h-4 w-4 text-blue-500" />
                  <span className="text-blue-600">Accéder V1</span>
                </DropdownMenuItem>
              )}
              {school.hasV2 && (() => {
                const v2Deployment = school.deployments?.find(d => d.version === 'v2');
                const tenantId = v2Deployment?.v2TenantId;
                return (
                  <DropdownMenuItem onClick={() => {
                    const url = tenantId
                      ? `https://my.ecole-futee.com/?school-connect=${tenantId}`
                      : 'https://my.ecole-futee.com';
                    window.open(url, '_blank');
                  }}>
                    <ExternalLink className="mr-2 h-4 w-4 text-green-500" />
                    <span className="text-green-600">Accéder V2</span>
                  </DropdownMenuItem>
                );
              })()}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate, isFavorite, toggleFavorite, filters.favorites, updateFilters, expandedSchools, toggleExpanded]
  );

  // Sous-liste des établissements (affichée quand l'école est dépliée)
  const renderSubRow = useCallback(
    (school: School) => {
      if (!expandedSchools.has(school.id) || !school.establishments?.length) {
        return null;
      }
      return (
        <div className="bg-blue-50/40 px-14 py-2">
          {school.establishments.map((establishment) => (
            <Link
              key={establishment.id}
              to={`/schools/${school.code}?tab=etablissements`}
              className="flex items-center gap-3 py-1.5 text-sm text-gray-700 hover:text-primary group"
            >
              <Building2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary flex-shrink-0" />
              <span className="font-medium">{establishment.name}</span>
              {establishment.isPrincipal && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  Principal
                </Badge>
              )}
              {establishment.rne && (
                <span className="font-mono text-xs text-gray-500">{establishment.rne}</span>
              )}
              {establishment.address && (
                <span className="text-xs text-gray-400 truncate">{establishment.address}</span>
              )}
            </Link>
          ))}
        </div>
      );
    },
    [expandedSchools]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <SchoolIcon className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Écoles
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gérez les écoles de votre portefeuille
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/schools/map">
              <MapPin className="mr-2 h-4 w-4" />
              Carte
            </Link>
          </Button>
          <ExportButton
            exportType="schools"
            filters={{
              status: filters.status,
              vip: filters.vip,
              version: filters.version,
              search: filters.search,
            }}
          >
            Exporter
          </ExportButton>
          {hasRole('ROLE_SUPERADMIN') && (
            <>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Synchroniser la configuration
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Configuration des environnements</AlertDialogTitle>
                    <AlertDialogDescription>
                      {syncResult === null && 'Confirmez-vous la synchronisation de la configuration des environnements en production ?'}
                      {syncResult === 'ok' && (
                        <span className="text-emerald-600 font-medium">Configuration synchronisée avec succès.</span>
                      )}
                      {syncResult === 'nok' && (
                        <span className="text-red-600 font-medium">La synchronisation a rencontré des erreurs.</span>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    {syncResult === null ? (
                      <>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); handleSyncConfiguration(); }} disabled={syncLoading}>
                          {syncLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Oui, synchroniser
                        </AlertDialogAction>
                      </>
                    ) : (
                      <AlertDialogCancel onClick={() => setSyncResult(null)}>Fermer</AlertDialogCancel>
                    )}
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          <Button asChild>
            <Link to="/schools/new">
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle école
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="En production"
          value={stats?.production || 0}
          icon={CheckCircle2}
          color="green"
          onClick={() => updateFilters({ status: 'production', page: 1 })}
          isActive={filters.status === 'production'}
        />
        <StatCard
          title="En creation"
          value={stats?.created || 0}
          icon={Clock}
          color="blue"
          onClick={() => updateFilters({ status: 'created', page: 1 })}
          isActive={filters.status === 'created'}
        />
        <StatCard
          title="Demos"
          value={stats?.demo || 0}
          icon={PlayCircle}
          color="orange"
          onClick={() => updateFilters({ status: 'demo', page: 1 })}
          isActive={filters.status === 'demo'}
        />
        <StatCard
          title="Resiliees"
          value={stats?.cancelled || 0}
          icon={XCircle}
          color="red"
          onClick={() => updateFilters({ status: 'cancelled', page: 1 })}
          isActive={filters.status === 'cancelled'}
        />
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pt-4">
        {/* Search */}
        <div className="relative w-full sm:w-auto sm:min-w-[300px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Rechercher par nom, code, établissement ou RNE..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Select
            value={filters.status}
            onValueChange={(value) => updateFilters({ status: value, page: 1 })}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les écoles</SelectItem>
              <SelectItem value="active">Écoles actives</SelectItem>
              <SelectItem value="production">En production</SelectItem>
              <SelectItem value="created">En creation</SelectItem>
              <SelectItem value="demo">Demos</SelectItem>
              <SelectItem value="cancelled">Resiliees</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.vip || 'all'}
            onValueChange={(value) => updateFilters({ vip: value === 'all' ? '' : value, page: 1 })}
          >
            <SelectTrigger className="w-full sm:w-[140px]">
              <SelectValue placeholder="VIP" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="true">VIP uniquement</SelectItem>
              <SelectItem value="false">Non VIP</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.version || 'all'}
            onValueChange={(value) => updateFilters({ version: value === 'all' ? '' : value, page: 1 })}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Version" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes versions</SelectItem>
              <SelectItem value="v1">V1 uniquement</SelectItem>
              <SelectItem value="v2">V2 uniquement</SelectItem>
              <SelectItem value="both">V1 et V2</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.network || 'all'}
            onValueChange={(value) => updateFilters({ network: value === 'all' ? '' : value, page: 1 })}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Réseau" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les réseaux</SelectItem>
              {networks.map((n) => (
                <SelectItem key={n.id} value={String(n.id)}>
                  {n.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.academie || 'all'}
            onValueChange={(value) => updateFilters({ academie: value === 'all' ? '' : value, page: 1 })}
          >
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Académie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les académies</SelectItem>
              {academies.map((a) => (
                <SelectItem key={a.code} value={a.code}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={schools}
        loading={loading}
        page={filters.page}
        pageSize={filters.limit}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={(page) => updateFilters({ page })}
        onPageSizeChange={(limit) => updateFilters({ limit, page: 1 })}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
        keyExtractor={(school) => school.id}
        emptyMessage="Aucune école trouvée"
        renderSubRow={renderSubRow}
      />
    </div>
  );
}
